import React from 'react';
import { FormGroup, Label, Input, Row, Col, FormFeedback } from 'reactstrap';
import DatePicker from 'react-datepicker';
import { CORE_VALUES } from './formConstants';

interface FormData {
  name: string;
  dateOfIncident: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  descriptionOfOneOnOne: string;
  coreValues: string[];
  meetingGoal: string;
  privateNotes: string;
}

interface FormErrors {
  name?: string;
  dateOfIncident?: string;
  policyViolationIds?: string;
  descriptionOfOneOnOne?: string;
  coreValues?: string;
  meetingGoal?: string;
}

interface PolicyViolation {
  id: string;
  name: string;
}

interface UserInfoSectionProps {
  formData: FormData;
  errors: FormErrors;
  onChange: (field: keyof FormData, value: string) => void;
}

export const UserInfoSection = ({ formData, errors, onChange }: UserInfoSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Employee Information</h5>
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="name">
            Name <span className="text-danger">*</span>
          </Label>
          <Input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => onChange('name', e.target.value)}
            invalid={!!errors.name}
            readOnly
            disabled
          />
          {errors.name && <FormFeedback>{errors.name}</FormFeedback>}
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="dateOfIncident">
            Date of Incident <span className="text-danger">*</span>
          </Label>
          <Input
            tag={DatePicker}
            dateFormat="MM/dd/yyyy"
            id="dateOfIncident"
            selected={formData.dateOfIncident ? new Date(formData.dateOfIncident + 'T00:00:00') : null}
            onChange={(d: any) => onChange('dateOfIncident', d ? d.toISOString().split('T')[0] : '')}
            maxDate={new Date()}
            placeholderText="Select date"
            autoComplete="off"
          />
          {errors.dateOfIncident && <FormFeedback>{errors.dateOfIncident}</FormFeedback>}
        </FormGroup>
      </Col>
    </Row>
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="position">Position</Label>
          <Input
            type="text"
            id="position"
            value={formData.position}
            onChange={(e) => onChange('position', e.target.value)}
            readOnly
            disabled
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="department">Department</Label>
          <Input
            type="text"
            id="department"
            value={formData.department}
            onChange={(e) => onChange('department', e.target.value)}
            readOnly
            disabled
          />
        </FormGroup>
      </Col>
    </Row>
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="supervisor">Supervisor</Label>
          <Input
            type="text"
            id="supervisor"
            value={formData.supervisor}
            readOnly
            onChange={(e) => onChange('supervisor', e.target.value)}
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="dateOfHire">Date of Hire (Read-only)</Label>
          <Input type="date" id="dateOfHire" value={formData.dateOfHire} readOnly disabled />
        </FormGroup>
      </Col>
    </Row>
  </div>
);

interface PolicySectionProps {
  formData: FormData;
  errors: FormErrors;
  policyViolations: PolicyViolation[];
  onChange: (field: keyof FormData, value: string | string[]) => void;
}

export const PolicySection = ({ formData, errors, policyViolations, onChange }: PolicySectionProps) => {
  const handleCheckboxChange = (violationId: string) => {
    const newIds = formData.policyViolationIds.includes(violationId)
      ? formData.policyViolationIds.filter((id) => id !== violationId)
      : [...formData.policyViolationIds, violationId];
    onChange('policyViolationIds', newIds);
  };

  return (
    <div className="mb-4">
      <h5 className="text-primary mb-3">Policy Violations</h5>
      <FormGroup>
        <Label>
          Policy Violations <span className="text-danger">*</span>
        </Label>
        <div>
          {policyViolations.map((violation) => (
            <FormGroup check key={violation.id} className="mb-2">
              <Label check>
                <Input
                  type="checkbox"
                  checked={formData.policyViolationIds.includes(violation.id)}
                  onChange={() => handleCheckboxChange(violation.id)}
                />
                {violation.name}
              </Label>
            </FormGroup>
          ))}
        </div>
        {errors.policyViolationIds && (
          <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
            {errors.policyViolationIds}
          </div>
        )}
      </FormGroup>
    </div>
  );
};

interface DescriptionSectionProps {
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

export const DescriptionSection = ({ value, error, onChange }: DescriptionSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Description of One On One</h5>
    <FormGroup>
      <Label for="descriptionOfOneOnOne">
        Description <span className="text-danger">*</span>
      </Label>
      <Input
        type="textarea"
        id="descriptionOfOneOnOne"
        rows={6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        invalid={!!error}
        placeholder="Provide a detailed description of the conversation or topic..."
      />
      {error && <FormFeedback>{error}</FormFeedback>}
    </FormGroup>
  </div>
);

interface CoreValueSectionProps {
  selectedValues: string[];
  error?: string;
  onChange: (values: string[]) => void;
}

export const CoreValueSection = ({ selectedValues, error, onChange }: CoreValueSectionProps) => {
  const handleCheckboxChange = (valueId: string) => {
    const newValues = selectedValues.includes(valueId)
      ? selectedValues.filter((v) => v !== valueId)
      : [...selectedValues, valueId];
    onChange(newValues);
  };

  return (
    <div className="mb-4">
      <h5 className="text-primary mb-3">Core Value</h5>
      <FormGroup>
        <Label>
          What core value did you focus on? <span className="text-danger">*</span>
        </Label>
        <div>
          {CORE_VALUES.map((value) => (
            <FormGroup check key={value.id} className="mb-2">
              <Label check>
                <Input
                  type="checkbox"
                  checked={selectedValues.includes(value.id)}
                  onChange={() => handleCheckboxChange(value.id)}
                />
                {value.label}
              </Label>
            </FormGroup>
          ))}
        </div>
        {error && (
          <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
            {error}
          </div>
        )}
      </FormGroup>
    </div>
  );
};

interface MeetingGoalSectionProps {
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

export const MeetingGoalSection = ({ value, error, onChange }: MeetingGoalSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Meeting Goal</h5>
    <FormGroup>
      <Label for="meetingGoal">
        What is the goal for this meeting? <span className="text-danger">*</span>
      </Label>
      <Input
        type="textarea"
        id="meetingGoal"
        rows={5}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        invalid={!!error}
        placeholder="Describe the goal for this meeting..."
      />
      {error && <FormFeedback>{error}</FormFeedback>}
    </FormGroup>
  </div>
);

interface PrivateNotesSectionProps {
  value: string;
  onChange: (value: string) => void;
}

export const PrivateNotesSection = ({ value, onChange }: PrivateNotesSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Private Notes</h5>
    <FormGroup>
      <Label for="privateNotes">Private Notes</Label>
      <Input
        type="textarea"
        id="privateNotes"
        rows={5}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="These notes are only visible to Director and Upper Management..."
      />
      <small className="form-text text-muted">
        <i className="fas fa-lock mr-1" />
        Only seen by Director and Upper Management
      </small>
    </FormGroup>
  </div>
);
